import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import styles from './AdminDashboard.module.css';
import { 
  FiUsers, FiUserCheck, FiUserPlus, FiMail, FiPhone, 
  FiCalendar, FiActivity, FiSearch, FiFilter, 
  FiDownload, FiEye, FiTrash2, FiShield,
  FiTrendingUp, FiClock, FiCheckCircle, FiXCircle,
  FiArrowLeft, FiRefreshCw, FiMoreVertical, FiBriefcase, FiUser
} from 'react-icons/fi';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsersThisMonth: 0,
    activeUsers: 0,
    totalRoles: {}
  });

  useEffect(() => {
    checkAdminAndFetchData();
  }, []);

  const checkAdminAndFetchData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/login');
        return;
      }

      // Check if user is admin
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('is_admin')
        .eq('id', session.user.id)
        .single();
      
      const adminEmails = ['brandversetechnologies@gmail.com'];
      const userIsAdmin = profile?.is_admin === true || adminEmails.includes(session.user.email);
      
      if (!userIsAdmin) {
        navigate('/');
        return;
      }
      
      setIsAdmin(true);
      await fetchAllUsers();
    } catch (error) {
      console.error('Error checking admin status:', error);
      navigate('/');
    }
  };

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      // First try to fetch from user_profiles table (works without admin API)
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        setUsers([]);
        setFilteredUsers([]);
        setLoading(false);
        return;
      }

      // Format users from profiles
      const formattedUsers = profiles.map(profile => ({
        id: profile.id,
        email: profile.email,
        name: profile.name || 'Not provided',
        phone: profile.phone || 'Not provided',
        role: profile.role || 'Not specified',
        created_at: profile.created_at,
        last_sign_in: profile.updated_at,
        confirmed: true,
        activity_count: 0,
        last_activity: profile.updated_at
      }));

      setUsers(formattedUsers);
      setFilteredUsers(formattedUsers);
      calculateStats(formattedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (usersList) => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const newUsersThisMonth = usersList.filter(user => {
      const userDate = new Date(user.created_at);
      return userDate.getMonth() === currentMonth && userDate.getFullYear() === currentYear;
    }).length;

    const roles = {};
    usersList.forEach(user => {
      const role = user.role;
      roles[role] = (roles[role] || 0) + 1;
    });

    setStats({
      totalUsers: usersList.length,
      newUsersThisMonth: newUsersThisMonth,
      activeUsers: usersList.filter(u => u.activity_count > 0).length,
      totalRoles: roles
    });
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    const filtered = users.filter(user => 
      user.name.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term) ||
      user.role.toLowerCase().includes(term) ||
      user.phone.includes(term)
    );
    setFilteredUsers(filtered);
  };

  const handleRefresh = () => {
    fetchAllUsers();
  };

  const handleExport = () => {
    if (filteredUsers.length === 0) return;
    
    const csvData = filteredUsers.map(user => ({
      Name: user.name,
      Email: user.email,
      Phone: user.phone,
      Role: user.role,
      'Joined Date': new Date(user.created_at).toLocaleDateString(),
      'Last Active': new Date(user.last_activity).toLocaleDateString(),
      'Activities': user.activity_count
    }));

    const csvHeaders = Object.keys(csvData[0]).join(',');
    const csvRows = csvData.map(row => Object.values(row).join(','));
    const csvContent = [csvHeaders, ...csvRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'Student': '#3b82f6',
      'IT Professional': '#10b981',
      'Business Owner': '#f59e0b',
      'Entrepreneur': '#8b5cf6',
      'Startup Founder': '#ec4899',
      'Other': '#6b7280'
    };
    return colors[role] || '#6b7280';
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className={styles.adminDashboard}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button onClick={() => navigate('/')} className={styles.backButton}>
              <FiArrowLeft /> Back to Home
            </button>
            <h1>Admin Dashboard</h1>
            <p>Manage and monitor all users</p>
          </div>
          <div className={styles.headerRight}>
            <button onClick={handleRefresh} className={styles.refreshButton}>
              <FiRefreshCw /> Refresh
            </button>
            <button onClick={handleExport} className={styles.exportButton}>
              <FiDownload /> Export
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
              <FiUsers />
            </div>
            <div className={styles.statInfo}>
              <h3>Total Users</h3>
              <p className={styles.statNumber}>{stats.totalUsers}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#10b981' }}>
              <FiUserPlus />
            </div>
            <div className={styles.statInfo}>
              <h3>New This Month</h3>
              <p className={styles.statNumber}>{stats.newUsersThisMonth}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
              <FiActivity />
            </div>
            <div className={styles.statInfo}>
              <h3>Active Users</h3>
              <p className={styles.statNumber}>{stats.activeUsers}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#8b5cf6' }}>
              <FiTrendingUp />
            </div>
            <div className={styles.statInfo}>
              <h3>Engagement Rate</h3>
              <p className={styles.statNumber}>
                {stats.totalUsers ? Math.round((stats.activeUsers / stats.totalUsers) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>

        {/* Roles Distribution */}
        <div className={styles.rolesSection}>
          <h2>User Roles Distribution</h2>
          <div className={styles.rolesGrid}>
            {Object.entries(stats.totalRoles).map(([role, count]) => (
              <div key={role} className={styles.roleCard}>
                <div 
                  className={styles.roleColor} 
                  style={{ background: getRoleBadgeColor(role) }}
                ></div>
                <div className={styles.roleInfo}>
                  <span className={styles.roleName}>{role || 'Not specified'}</span>
                  <span className={styles.roleCount}>{count} users</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Search and Filter */}
        <div className={styles.searchSection}>
          <div className={styles.searchBar}>
            <FiSearch />
            <input
              type="text"
              placeholder="Search by name, email, role, or phone..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className={styles.resultsInfo}>
            Showing {filteredUsers.length} of {users.length} users
          </div>
        </div>

        {/* Users Table */}
        <div className={styles.tableContainer}>
          {filteredUsers.length > 0 ? (
            <table className={styles.usersTable}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Last Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className={styles.userCell}>
                        <div className={styles.userAvatar}>
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className={styles.userName}>{user.name}</div>
                          <div className={styles.userEmail}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className={styles.contactCell}>
                        <FiPhone className={styles.contactIcon} />
                        {user.phone}
                      </div>
                    </td>
                    <td>
                      <span 
                        className={styles.roleBadge}
                        style={{ background: getRoleBadgeColor(user.role) }}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <div className={styles.dateCell}>
                        <FiCalendar className={styles.dateIcon} />
                        {formatDate(user.created_at).split(',')[0]}
                      </div>
                    </td>
                    <td>
                      <div className={styles.dateCell}>
                        <FiClock className={styles.dateIcon} />
                        {formatDate(user.last_activity).split(',')[0]}
                      </div>
                    </td>
                    <td>
                      <button 
                        onClick={() => handleViewUser(user)}
                        className={styles.viewButton}
                      >
                        <FiEye /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.noResults}>
              <FiSearch />
              <p>No users found matching your search</p>
            </div>
          )}
        </div>
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className={styles.modalOverlay} onClick={() => setShowUserModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>User Details</h2>
              <button onClick={() => setShowUserModal(false)} className={styles.closeButton}>
                <FiXCircle />
              </button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.userDetailCard}>
                <div className={styles.userDetailAvatar}>
                  {selectedUser.name.charAt(0).toUpperCase()}
                </div>
                <h3>{selectedUser.name}</h3>
                <p className={styles.userDetailEmail}>{selectedUser.email}</p>
              </div>

              <div className={styles.detailSection}>
                <h4>Personal Information</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <FiUser />
                    <div>
                      <label>Full Name</label>
                      <p>{selectedUser.name}</p>
                    </div>
                  </div>
                  <div className={styles.detailItem}>
                    <FiMail />
                    <div>
                      <label>Email Address</label>
                      <p>{selectedUser.email}</p>
                    </div>
                  </div>
                  <div className={styles.detailItem}>
                    <FiPhone />
                    <div>
                      <label>Phone Number</label>
                      <p>{selectedUser.phone}</p>
                    </div>
                  </div>
                  <div className={styles.detailItem}>
                    <FiBriefcase />
                    <div>
                      <label>Role</label>
                      <p>{selectedUser.role}</p>
                    </div>
                  </div>
                  <div className={styles.detailItem}>
                    <FiCalendar />
                    <div>
                      <label>Member Since</label>
                      <p>{formatDate(selectedUser.created_at)}</p>
                    </div>
                  </div>
                  <div className={styles.detailItem}>
                    <FiActivity />
                    <div>
                      <label>Last Active</label>
                      <p>{formatDate(selectedUser.last_activity)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4>Account Status</h4>
                <div className={styles.statusBadge}>
                  {selectedUser.confirmed ? (
                    <>
                      <FiCheckCircle /> Email Verified
                    </>
                  ) : (
                    <>
                      <FiXCircle /> Email Not Verified
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;